/**
 * Tag application timeline panel for the Qualified Lead detail page.
 *
 * The qualifier worker writes a single `updated_at` per qualification row
 * rather than per-tag timestamps, so we render each tag with the same
 * timestamp here. The wireframe is fine with that — finer-grained per-tag
 * timestamps could be added later if the worker starts emitting them.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tag, Check } from 'lucide-react'

interface Props {
  readonly tags: readonly string[] | null
  readonly appliedAt: string
}

export function TagTimelinePanel({ tags, appliedAt }: Props) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Tag className="h-4 w-4" />
          Tag application timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!tags || tags.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tags applied.</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {tags.map((tag) => (
              <li key={tag} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <code className="font-mono text-xs bg-muted/50 px-1.5 py-0.5 rounded">
                  {tag}
                </code>
                <span className="text-muted-foreground">applied</span>
                <time
                  className="ml-auto text-xs text-muted-foreground"
                  dateTime={appliedAt}
                  title={new Date(appliedAt).toLocaleString()}
                >
                  {new Date(appliedAt).toLocaleString()}
                </time>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
